import type { FC, ReactNode, JSX } from "react";

import classNames from "classnames";
import { kebabCase } from "lodash";
import styled, { css } from "styled-components";

import type { IconName } from "@similarweb/icons";
import { SWReactCountryIcons, SWReactIcons } from "@similarweb/icons";
import { formatDate } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";

import { i18nFilter } from "app/filters/i18nFilter";
import CountryService from "app/services/CountryService";

import { CenteredFlexColumn, FlexRow } from "../../../styled components/StyledFlex/src/StyledFlex";
import { StyledLink } from "../../../styled components/StyledLink/src/StyledLink";

import { LabelText } from "./styled";

export const IconItem = styled(FlexRow)<{ first?: boolean }>`
    align-items: center;
    ${({ onClick, onMouseEnter }) => (onClick || onMouseEnter) && "cursor: pointer;"}

    svg {
        position: relative;
        height: 16px;
        width: 16px;
        margin-right: 8px;
        margin-left: ${(props) => (props.first ? "0px" : "8px")};
        display: block;
    }
`;
IconItem.displayName = "IconItem";

export const DefaultItem = styled(FlexRow)<{ first?: boolean }>`
    ${({ onClick, onMouseEnter }) => (onClick || onMouseEnter) && "cursor: pointer;"}
    &:before {
        content: "•";
        margin-right: 8px;
        margin-left: 8px;
    }
    ${(props) =>
        props.first &&
        css`
            &:before {
                content: "";
                margin-right: 0;
                margin-left: 0;
            }
        `}
    span {
        color: ${colorsPalettes.carbon["200"]};
        margin-left: 6px;
        margin-right: 6px;
    }
`;
DefaultItem.displayName = "DefaultItem";

const Bullet = styled(CenteredFlexColumn)<{ backgroundColor: string }>`
    margin: auto 5px auto auto;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ backgroundColor }) => (backgroundColor ? `${backgroundColor}` : `transparent`)};
`;

type ColoredBullet = string | JSX.Element;

interface DateValue {
    compareFrom?: string;
    compareTo?: string;
    from: string;
    to: string;
    useRangeDisplay?: boolean;
    dateSeparator?: string;
    coloredBullets?: {
        dateBullet?: ColoredBullet;
        compareDateBullet?: ColoredBullet;
    };
}

type FilterValue = string | DateValue | (() => ReactNode) | any;

export interface Filter {
    filter: string;
    value: FilterValue;
    getFilterValue?: (params: {
        filter: Filter;
        index: number;
        isFirst: boolean;
        className?: string;
        filters: Filter[];
    }) => JSX.Element;
    label?: string;
    onClick?: () => void;
    onMouseEnter?: () => void;
    countryCode?: number;
    href?: string;
    target?: string;
    iconName?: IconName;
    className?: string;
    displayName?: string;
    format?: string;
}

interface IBoxSubtitleProps {
    filters: Filter[] | unknown;
    className?: string;
}

const US_COUNTRY_ID = 840;

const BoxSubtitle: FC<IBoxSubtitleProps> = ({ className, filters }) => {
    const text = (value: FilterValue, href?: string, target?: string) =>
        href ? (
            <StyledLink
                href={href}
                target={target}
            >
                {value}
            </StyledLink>
        ) : (
            <span>{value}</span>
        );
    const webSourceDisplayName = (webSource: string) => {
        const displayNameKey = "websources." + webSource.toLowerCase();
        return i18nFilter()(displayNameKey);
    };
    if (!Array.isArray(filters)) {
        return null; // added this to prevent typescript error, after removing the "unknown" type this could probably be removed.
    }
    const items = filters.map((item, idx) => {
        const isFirst = idx === 0;
        if (typeof item.getFilterValue === "function") {
            return item.getFilterValue({ filter: item, index: idx, isFirst, className, filters });
        }
        if (item.value) {
            if (item.filter === "lastDate") {
                return (
                    <IconItem
                        key={idx}
                        first={isFirst}
                        onClick={item.onClick}
                        onMouseEnter={item.onMouseEnter}
                    >
                        <SWReactIcons iconName="daily-ranking" />
                        <span>{i18nFilter()("subtitle.last.update")}</span>&nbsp;
                        <span>{item.value}</span>
                    </IconItem>
                );
            }
            if (item.filter === "country") {
                const fixedCountryCode = CountryService.isUSState(item.countryCode) ? US_COUNTRY_ID : item.countryCode;
                return (
                    <IconItem
                        key={idx}
                        first={isFirst}
                        onClick={item.onClick}
                        onMouseEnter={item.onMouseEnter}
                    >
                        <SWReactCountryIcons
                            countryCode={fixedCountryCode}
                            className="subtitle-country"
                        />
                        {text(item.value, item.href, item.target)}
                    </IconItem>
                );
            }
            if (item.filter === "icon") {
                return (
                    <IconItem
                        key={idx}
                        first={isFirst}
                        onClick={item.onClick}
                        onMouseEnter={item.onMouseEnter}
                    >
                        <SWReactIcons
                            iconName={item.iconName}
                            className={classNames("subtitle-icon", item.className)}
                        />
                        {text(item.value, item.href, item.target)}
                    </IconItem>
                );
            } else if (item.filter === "date") {
                const isPoP = item.value.compareFrom && item.value.compareTo;

                const { dateBullet, compareDateBullet } = item.value?.coloredBullets || {};

                const RenderBullet = ({ bullet }: { bullet: ColoredBullet }) =>
                    typeof bullet === "string" ? <Bullet backgroundColor={bullet} /> : bullet;

                return (
                    <DefaultItem
                        key={idx}
                        first={isFirst}
                        onClick={item.onClick}
                        onMouseEnter={item.onMouseEnter}
                    >
                        {isPoP ? (
                            <>
                                <IconItem first={true}>
                                    <SWReactIcons
                                        iconName="daily-ranking"
                                        className="subtitle-icon"
                                    />
                                    {compareDateBullet ? <RenderBullet bullet={compareDateBullet} /> : null}
                                    {formatDate(
                                        item.value.compareFrom,
                                        item.value.compareTo,
                                        item.format,
                                        item.value?.useRangeDisplay,
                                        item.value?.dateSeparator,
                                    )}
                                </IconItem>
                                <span> {i18nFilter()("global.vs")}</span>
                            </>
                        ) : null}
                        <IconItem first={!isPoP}>
                            {!isPoP ? (
                                <SWReactIcons
                                    iconName="daily-ranking"
                                    className="subtitle-icon"
                                />
                            ) : null}
                            {dateBullet ? <RenderBullet bullet={dateBullet} /> : null}
                            {formatDate(
                                item.value.from,
                                item.value.to,
                                item.format,
                                item.value?.useRangeDisplay,
                                item.value?.dateSeparator,
                            )}
                            {item.label ? <LabelText>{item.label}</LabelText> : null}
                        </IconItem>
                    </DefaultItem>
                );
            } else if (item.filter === "store") {
                const iconName = item.value === "Google" ? "google-play" : "i-tunes";
                const store = item.value === "Google" ? "Google Play" : "Apple";
                return (
                    <IconItem
                        key={idx}
                        first={isFirst}
                        onClick={item.onClick}
                        onMouseEnter={item.onMouseEnter}
                    >
                        <SWReactIcons
                            iconName={iconName}
                            className="subtitle-store"
                        />
                        {text(store, item.href, item.target)}
                    </IconItem>
                );
            } else if (item.filter === "webSource") {
                const icon = item.value === "Total" ? "combined" : item.value;
                const iconName = kebabCase(icon) as IconName;
                const value = item.displayName ? item.displayName : webSourceDisplayName(item.value);
                return (
                    <IconItem
                        key={idx}
                        first={isFirst}
                        onClick={item.onClick}
                        onMouseEnter={item.onMouseEnter}
                    >
                        <SWReactIcons
                            iconName={iconName}
                            className="subtitle-websource"
                        />
                        {text(value, item.href, item.target)}
                    </IconItem>
                );
            } else {
                if (typeof item.value === "function") {
                    return (
                        <item.value
                            key={idx}
                            first={isFirst}
                        />
                    );
                }
                return (
                    <DefaultItem
                        key={idx}
                        first={isFirst}
                        onClick={item.onClick}
                        onMouseEnter={item.onMouseEnter}
                    >
                        {text(item.value, item.href, item.target)}
                    </DefaultItem>
                );
            }
        }
    });

    return <FlexRow className={className}>{items}</FlexRow>;
};
BoxSubtitle.displayName = "BoxSubtitle";
export default BoxSubtitle;
